import * as React from "react"
import { Filter, X, ChevronDown } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { AlfredDevicesTable } from "@/components/alfred/alfred-devices-table"
import { AddAlfredDeviceForm } from "@/components/alfred/add-alfred-device-form"
import { cloneDeviceToFormData, type AlfredDeviceFormData } from "@/components/alfred/alfred-device-form-fields"
import { DeviceSearchBar } from "@/components/alfred/device-search-bar"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { BulkUpdateBar, type BulkValue } from "@/components/alfred/bulk-update-bar"
import { BulkUpdateFailureDescription } from "@/components/alfred/bulk-update-failure-description"
import { DeleteAlfredDeviceDialog } from "@/components/alfred/delete-alfred-device-dialog"
import { ColumnsToggle } from "@/components/alfred/columns-toggle"
import { DEFAULT_VISIBLE_COLUMNS, type ColumnKey } from "@/components/alfred/alfred-devices-table"
import {
  useAlfredDevices,
  useAlfredDevicesByClient,
  useBulkDeleteAlfredDevices,
  useBulkUpdateAlfredDevices,
  useCloneAlfredDevice,
  useCreateAlfredDevice,
  useDeleteAlfredDevice,
} from "@/hooks/use-alfred-devices"
import { useAlfredDeviceListControls } from "@/hooks/use-alfred-device-list-controls"
import { useAlfredBulkSelection } from "@/hooks/use-alfred-bulk-selection"
import { useAlfredTourBulkPreview } from "@/hooks/use-alfred-tour-bulk-preview"
import { useAlfredClients } from "@/hooks/use-alfred-clients"
import { alfredDeviceService } from "@/services/alfred-device-service"
import { useToast } from "@/hooks/use-toast"
import { getAlfredBulkUpdatePresentation } from "@/lib/alfred-bulk-update"
import {
  ALFRED_DEVICE_LIST_ORIGIN_STATE,
  getAlfredDeviceListHistoryState,
  type AlfredDeviceListHistoryState,
} from "@/lib/alfred-device-navigation"
import {
  getAlfredBulkDeleteFailedEntries,
  getAlfredPageAfterBulkDelete,
  getAlfredBulkDeletePresentation,
} from "@/lib/alfred-bulk-delete"
import {
  toAlfredDeviceUI,
  type AlfredDeviceUI,
  type CloneAlfredDeviceRequest,
  type CreateAlfredDeviceRequest,
} from "@/types/alfred-device"
import { isNotFoundError } from "@/types/api"

export function AlfredDevicesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const restoredListState = getAlfredDeviceListHistoryState(location.state)

  const [addDeviceOpen, setAddDeviceOpen] = React.useState(false)
  const [cloneData, setCloneData] = React.useState<AlfredDeviceFormData | null>(null)
  const [cloneSourceDevicePk, setCloneSourceDevicePk] = React.useState<number | null>(null)
  const [clientFilterOpen, setClientFilterOpen] = React.useState(false)
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(
    () => restoredListState?.selectedClientId ?? null,
  )
  const [bulkEditorOpen, setBulkEditorOpen] = React.useState(false)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = React.useState(false)
  const [visibleColumns, setVisibleColumns] = React.useState<Set<ColumnKey>>(new Set(DEFAULT_VISIBLE_COLUMNS))

  const controls = useAlfredDeviceListControls(restoredListState?.controls)

  // Keep this view on its current history entry so a detail-page return can remount it.
  React.useEffect(() => {
    const alfredDeviceList: AlfredDeviceListHistoryState = {
      controls: {
        page: controls.page,
        pageSize: controls.pageSize,
        filter: controls.filter,
        searchInput: controls.searchInput,
        sortBy: controls.sortBy,
        sortOrder: controls.sortOrder,
      },
      selectedClientId,
    }

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: { alfredDeviceList } },
    )
  }, [
    controls.filter,
    controls.page,
    controls.pageSize,
    controls.searchInput,
    controls.sortBy,
    controls.sortOrder,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    selectedClientId,
  ])
  const selectionScope = JSON.stringify([
    selectedClientId,
    controls.page,
    controls.pageSize,
    controls.filter,
    controls.sortBy,
    controls.sortOrder,
  ])
  const handleSelectionLimitReached = React.useCallback(() => {
    toast({
      variant: "warning",
      title: "Selection limit reached",
      description: "You can select up to 200 devices at a time.",
    })
  }, [toast])
  const {
    selectedDevices,
    clear: clearSelectedDevices,
    replaceForScope: replaceSelectedDevicesForScope,
    toggle: toggleSelectedDevice,
    toggleAll: toggleAllSelectedDevices,
  } = useAlfredBulkSelection(selectionScope, handleSelectionLimitReached)

  // When a client filter is active we read from the by-client endpoint and disable the
  // all-devices fetch; otherwise the reverse. Search and sort params flow to whichever
  // endpoint is active.
  const allDevices = useAlfredDevices(controls.params, selectedClientId === null)
  const clientDevices = useAlfredDevicesByClient(
    selectedClientId ?? 0,
    controls.params,
    selectedClientId !== null,
  )
  const { data: response, error, isLoading, mutate: refetch } =
    selectedClientId === null ? allDevices : clientDevices

  const { data: clients, mutate: refetchClients } = useAlfredClients()
  const createDevice = useCreateAlfredDevice()
  const cloneDevice = useCloneAlfredDevice()
  const deleteDevice = useDeleteAlfredDevice()
  const bulkUpdateDevices = useBulkUpdateAlfredDevices()
  const bulkDeleteDevices = useBulkDeleteAlfredDevices()

  const selectedClient = React.useMemo(
    () => clients?.find((c) => c.id === selectedClientId) ?? null,
    [clients, selectedClientId],
  )
  const isSelectedClientNotFound = selectedClientId !== null && isNotFoundError(error)
  const refreshedMissingClientId = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (!isSelectedClientNotFound) {
      refreshedMissingClientId.current = null
      return
    }
    if (refreshedMissingClientId.current === selectedClientId) return

    refreshedMissingClientId.current = selectedClientId
    void refetchClients().catch(() => undefined)
  }, [isSelectedClientNotFound, refetchClients, selectedClientId])

  const handleSelectClient = (numericId: number) => {
    setSelectedClientId(numericId)
    setClientFilterOpen(false)
    controls.setPage(1)
  }

  const handleClearClient = () => {
    setSelectedClientId(null)
    controls.setPage(1)
  }

  const clearAllFilters = () => {
    controls.clearSearch()
    setSelectedClientId(null)
  }

  const devices: AlfredDeviceUI[] = React.useMemo(() => {
    if (!response?.items) return []
    return response.items.map(toAlfredDeviceUI)
  }, [response])
  const deviceIdByPrimaryKey = React.useMemo(
    () => new Map(devices.map((device) => [device.id, device.deviceId])),
    [devices],
  )
  const deviceIdentityByPrimaryKey = React.useMemo(
    () => new Map(devices.map((device) => [
      device.id,
      { deviceId: device.deviceId, clientId: device.clientId },
    ])),
    [devices],
  )
  const selectedDeviceIds = React.useMemo(
    () => new Set(selectedDevices.keys()),
    [selectedDevices],
  )
  const selectedDeviceIdentities = React.useMemo(
    () => [...selectedDevices].map(([id, deviceId]) => (
      deviceIdentityByPrimaryKey.get(id) ?? {
        deviceId,
        clientId: "Unknown client",
      }
    )),
    [deviceIdentityByPrimaryKey, selectedDevices],
  )

  React.useEffect(() => {
    if (selectedDeviceIds.size === 0) setBulkEditorOpen(false)
  }, [selectedDeviceIds.size])

  const handleDeviceCreated = async (data: CreateAlfredDeviceRequest) => {
    try {
      await createDevice.trigger(data)
      setAddDeviceOpen(false)
      toast({ variant: "success", title: "Device created successfully", description: `Device ${data.deviceId} has been added to the system.` })
      refetch()
    } catch (err) {
      toast({ variant: "error", title: "Failed to create device", description: err instanceof Error ? err.message : "Please try again." })
    }
  }

  const handleDeviceCloned = async (
    sourceDevicePk: number,
    data: CloneAlfredDeviceRequest,
  ) => {
    try {
      await cloneDevice.trigger({ sourceDevicePk, data })
      setAddDeviceOpen(false)
      toast({
        variant: "success",
        title: "Device cloned successfully",
        description: `Device ${data.deviceId} has been added to the system.`,
      })
      refetch()
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to clone device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleViewDevice = (device: AlfredDeviceUI) => {
    navigate(`/alfred/devices/${device.id}`, {
      state: ALFRED_DEVICE_LIST_ORIGIN_STATE,
    })
  }

  const handleEditDevice = (device: AlfredDeviceUI) => {
    navigate(`/alfred/devices/${device.id}?edit=true`, {
      state: ALFRED_DEVICE_LIST_ORIGIN_STATE,
    })
  }

  const handleAddDevice = () => {
    setCloneData(null)
    setCloneSourceDevicePk(null)
    setAddDeviceOpen(true)
  }

  const handleCloneDevice = async (device: AlfredDeviceUI) => {
    try {
      const fresh = await alfredDeviceService.getById(device.id)
      const freshFormData = cloneDeviceToFormData(fresh)
      if (JSON.stringify(freshFormData) !== JSON.stringify(cloneDeviceToFormData(device))) {
        toast({
          variant: "warning",
          title: "Device data has changed",
          description: "This device was updated since the list loaded. The clone was pre-filled with the latest values — refresh to update the table.",
        })
      }
      setCloneData(freshFormData)
      setCloneSourceDevicePk(fresh.id)
      setAddDeviceOpen(true)
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to load device for cloning",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleFormOpenChange = (open: boolean) => {
    setAddDeviceOpen(open)
    if (!open) {
      setCloneData(null)
      setCloneSourceDevicePk(null)
    }
  }

  const handleDeleteDevice = async (device: AlfredDeviceUI) => {
    try {
      await deleteDevice.trigger(device.id)
      toast({ variant: "success", title: "Device deleted", description: `${device.deviceId} has been permanently removed.` })
      await refetch()
    } catch (err) {
      toast({ variant: "error", title: "Failed to delete device", description: err instanceof Error ? err.message : "Please try again." })
    }
  }

  const handleBulkSave = async (property: string, value: BulkValue) => {
    const submittedScope = selectionScope
    const submittedDevices = new Map(selectedDevices)
    const submittedDeviceIdentities = new Map(
      [...submittedDevices].map(([id, deviceId]) => [
        id,
        deviceIdentityByPrimaryKey.get(id) ?? {
          deviceId,
          clientId: "Unknown client",
        },
      ]),
    )

    try {
      const result = await bulkUpdateDevices.trigger({
        ids: [...submittedDevices.keys()],
        update: { [property]: value },
      })
      const presentation = getAlfredBulkUpdatePresentation(result)

      if (result.successCount > 0) void refetch()

      if (presentation.kind === "success") {
        toast({
          variant: presentation.variant,
          title: presentation.title,
          description: "Configuration changes have been applied.",
        })
        handleClearSelection()
        return
      }

      replaceSelectedDevicesForScope(
        submittedScope,
        result.failedDevices.map((failure) => [
          failure.id,
          submittedDevices.get(failure.id) ?? "Unknown device",
        ]),
      )
      toast({
        variant: presentation.variant,
        title: presentation.title,
        description: (
          <BulkUpdateFailureDescription
            result={result}
            deviceIdentityByPrimaryKey={submittedDeviceIdentities}
          />
        ),
        duration: presentation.duration,
      })
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update devices",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleBulkDelete = async () => {
    const submittedScope = selectionScope
    const submittedDevices = new Map(selectedDevices)

    try {
      const result = await bulkDeleteDevices.trigger([...submittedDevices.keys()])
      const pageAfterDelete = getAlfredPageAfterBulkDelete({
        page: controls.page,
        pageSize: controls.pageSize,
        total: response?.total ?? devices.length,
        deleted: result.deleted,
      })

      if (pageAfterDelete < controls.page) {
        controls.setPage(pageAfterDelete)
      } else {
        await refetch()
      }
      setBulkDeleteDialogOpen(false)

      const presentation = getAlfredBulkDeletePresentation(result)
      toast({
        variant: presentation.variant,
        title: presentation.title,
        description: presentation.description,
      })

      if (presentation.kind === "success") {
        handleClearSelection()
        return
      }

      replaceSelectedDevicesForScope(
        submittedScope,
        getAlfredBulkDeleteFailedEntries(result, submittedDevices),
      )
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to delete devices",
        description: err instanceof Error ? err.message : "Please try again.",
      })
    }
  }

  const handleClearSelection = React.useCallback(() => {
    setBulkEditorOpen(false)
    setBulkDeleteDialogOpen(false)
    clearSelectedDevices()
  }, [clearSelectedDevices])

  const handleToggleSelect = (id: number) => {
    const deviceId = deviceIdByPrimaryKey.get(id)
    if (deviceId) toggleSelectedDevice(id, deviceId)
  }

  const handleToggleAll = (ids: number[], checked: boolean) => {
    toggleAllSelectedDevices(
      ids.flatMap((id) => {
        const deviceId = deviceIdByPrimaryKey.get(id)
        return deviceId ? [[id, deviceId] as const] : []
      }),
      checked,
    )
  }

  const clientOptions = React.useMemo(() => {
    if (!clients) return []
    return clients.map((c) => ({ numericId: c.id, name: c.clientId }))
  }, [clients])

  useAlfredTourBulkPreview({
    devices,
    selectedDevices,
    selectionScope,
    bulkEditorOpen,
    setBulkEditorOpen,
    clearSelectedDevices,
    replaceSelectedDevicesForScope,
  })

  return (
    <>
      <DashboardHeader onAddDevice={handleAddDevice} />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <h1 className="text-2xl font-semibold text-foreground">Alfred Devices</h1>

        {/* Search + Filters */}
        <div className="space-y-3">
          <DeviceSearchBar
            value={controls.searchInput}
            onChange={controls.setSearchInput}
            onSubmit={controls.submitSearch}
            onClear={controls.clearSearch}
            activeFilter={controls.filter}
            disabled={bulkUpdateDevices.isMutating || bulkDeleteDevices.isMutating}
          />

          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <Popover open={clientFilterOpen} onOpenChange={setClientFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-tour="filter-client"
                    className="bg-card border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filter by Client
                    <ChevronDown className="h-4 w-4 ml-1 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-72 max-w-[calc(100vw-2rem)] p-2 bg-popover border-border"
                  align="start"
                >
                  <p className="text-xs text-muted-foreground px-2 pb-2">Show devices for a client</p>
                  <TooltipProvider delayDuration={300}>
                    <RadioGroup
                      value={selectedClientId !== null ? String(selectedClientId) : undefined}
                      className="max-h-72 overflow-y-auto overflow-x-hidden"
                    >
                      {clientOptions.map((option) => (
                        <Tooltip key={option.numericId}>
                          <TooltipTrigger asChild>
                            <div
                              className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary cursor-pointer"
                              onClick={() => handleSelectClient(option.numericId)}
                            >
                              <RadioGroupItem
                                value={String(option.numericId)}
                                id={`client-${option.numericId}`}
                                className="shrink-0"
                              />
                              <label
                                htmlFor={`client-${option.numericId}`}
                                className="min-w-0 flex-1 truncate text-sm text-foreground cursor-pointer"
                              >
                                {option.name}
                              </label>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className="max-w-[min(28rem,calc(100vw-2rem))] break-all"
                          >
                            {option.name}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </RadioGroup>
                  </TooltipProvider>
                </PopoverContent>
              </Popover>

              {selectedClient && (
                <Badge variant="secondary" className="gap-1 pr-1 w-fit">
                  <span className="max-w-[16rem] truncate">Client: {selectedClient.clientId}</span>
                  <button
                    type="button"
                    onClick={handleClearClient}
                    aria-label="Clear client filter"
                    className="rounded-full p-0.5 hover:bg-background/60"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <ColumnsToggle visibleColumns={visibleColumns} onChange={setVisibleColumns} />
          </div>
        </div>

        <AlfredDevicesTable
          devices={devices}
          isLoading={isLoading}
          error={error}
          isSelectedClientNotFound={isSelectedClientNotFound}
          onRetry={refetch}
          onClearClientFilter={handleClearClient}
          onAddDevice={handleAddDevice}
          onRowClick={handleViewDevice}
          onEdit={handleEditDevice}
          onClone={handleCloneDevice}
          onDelete={handleDeleteDevice}
          sortBy={controls.sortBy}
          sortOrder={controls.sortOrder}
          onSort={controls.handleSort}
          hasActiveFilter={controls.filter.length > 0 || selectedClientId !== null}
          onClearFilters={clearAllFilters}
          selectedIds={selectedDeviceIds}
          onToggleSelect={handleToggleSelect}
          onToggleAll={handleToggleAll}
          onBulkEdit={() => setBulkEditorOpen(true)}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onClearSelection={handleClearSelection}
          bulkEditor={bulkEditorOpen && selectedDeviceIds.size > 0 ? (
            <BulkUpdateBar
              selectedCount={selectedDeviceIds.size}
              onSave={handleBulkSave}
              onCancel={() => setBulkEditorOpen(false)}
              isSaving={bulkUpdateDevices.isMutating}
            />
          ) : null}
          total={response?.total}
          page={controls.page}
          pageSize={controls.pageSize}
          onPageChange={(page) => {
            clearSelectedDevices()
            controls.setPage(page)
          }}
          onPageSizeChange={(pageSize) => {
            clearSelectedDevices()
            controls.setPageSize(pageSize)
          }}
          visibleColumns={visibleColumns}
          isDeleting={deleteDevice.isMutating || bulkDeleteDevices.isMutating}
          isBulkDeleting={bulkDeleteDevices.isMutating}
          isInteractionDisabled={bulkUpdateDevices.isMutating || bulkDeleteDevices.isMutating}
        />
      </main>

      <AddAlfredDeviceForm
        open={addDeviceOpen}
        onOpenChange={handleFormOpenChange}
        onSubmit={handleDeviceCreated}
        onCloneSubmit={handleDeviceCloned}
        isSubmitting={createDevice.isMutating || cloneDevice.isMutating}
        clients={clients ?? []}
        initialData={cloneData}
        cloneSourceDevicePk={cloneSourceDevicePk}
      />

      <DeleteAlfredDeviceDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!bulkDeleteDevices.isMutating) setBulkDeleteDialogOpen(open)
        }}
        devices={selectedDeviceIdentities}
        onConfirm={handleBulkDelete}
        isLoading={bulkDeleteDevices.isMutating}
      />

    </>
  )
}
